import React, { useState, useEffect } from "react";
import { Check, KeyRound, Trash2, UserPlus } from "lucide-react";
import adminApi, { getAdminUsername } from "../adminApi";
import { MIN_PASSWORD_LENGTH } from "./ChangePasswordDialog";
import { useLanguage } from "../context/LanguageContext";
import { useDialogs } from "../context/DialogContext";
import "./UserManagement.css";

interface AdminUser {
  id: number;
  username: string;
  role: string;
  is_active: number;
  created_at: string;
}

interface AdminRole {
  id: number;
  role_name: string;
  permissions: string[];
}

const UserManagement: React.FC = () => {
  const { t, tf } = useLanguage();
  const { notify, confirm } = useDialogs();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("admin");

  const [activeTab, setActiveTab] = useState<"users" | "roles">("users");
  const currentUsername = getAdminUsername();

  // Reset hasła wybranego użytkownika (wiersz pod użytkownikiem)
  const [resetUserId, setResetUserId] = useState<number | null>(null);
  const [resetPassword, setResetPassword] = useState("");
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      const response = await adminApi.get("/users");
      setUsers(response.data);
    } catch (err) {
      setError(t("Nie udało się pobrać użytkowników"));
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await adminApi.get("/roles");
      setRoles(response.data);
    } catch (err) {
      setError(t("Nie udało się pobrać ról"));
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchUsers(), fetchRoles()]);
      setLoading(false);
    };
    init();
    // użytkownicy i role raz, przy wejściu do widoku (fetch* to zwykłe funkcje komponentu)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newUsername || !newPassword) return;
    try {
      await adminApi.post("/users", {
        username: newUsername,
        password: newPassword,
        role: newRole
      });
      setNewUsername("");
      setNewPassword("");
      setNewRole("admin");
      setShowAddForm(false);
      setError(null);
      fetchUsers();
    } catch (err: any) {
      setError(
        err?.response?.status === 409
          ? t("A user with this login already exists.")
          : t("Nie udało się dodać użytkownika")
      );
    }
  };

  const handleResetPassword = async (e: React.FormEvent, user: AdminUser) => {
    e.preventDefault();
    setError(null);
    if (resetPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t("The new password must be at least 8 characters long."));
      return;
    }
    try {
      await adminApi.put(`/users/${user.id}/password`, { password: resetPassword });
      setResetUserId(null);
      setResetPassword("");
      setResetMessage(`${t("Password changed for")} ${user.username}.`);
    } catch (err) {
      setError(t("Failed to change password."));
    }
  };

  const handleDeleteUser = async (id: number, username: string) => {
    if (username === "admin") {
      notify(t("Nie można usunąć głównego konta admina"), "error");
      return;
    }
    if (await confirm({ message: tf("Are you sure you want to delete the user \"{name}\"?", { name: username }), confirmLabel: t("Delete"), danger: true })) {
      try {
        await adminApi.delete(`/users/${id}`);
        fetchUsers();
      } catch (err: any) {
        setError(
          err?.response?.data?.error === "You cannot delete your own account"
            ? t("You cannot delete your own account.")
            : t("Nie udało się usunąć użytkownika")
        );
      }
    }
  };

  const handleTogglePermission = async (roleId: number, permissions: string[], permission: string) => {
    const newPermissions = permissions.includes(permission)
      ? permissions.filter((p) => p !== permission)
      : [...permissions, permission];

    try {
      await adminApi.put(`/roles/${roleId}`, { permissions: newPermissions });
      fetchRoles();
    } catch (err) {
      setError(t("Nie udało się zaktualizować uprawnień"));
    }
  };

  // Te same nazwy sprawdza API (Backend/src/middleware/permissions.js)
  const allPossiblePermissions: string[] = [
    "Lock types", "Lock properties", "System options", "Supplier Icon", "Database preview", "Backups", "Tax change", "Database update", "Users", "Carts", "Mail delivery"
  ];

  const switchTab = (tab: "users" | "roles") => {
    setActiveTab(tab);
    setError(null);
    setResetMessage(null);
    setResetUserId(null);
  };

  if (loading) return <div className="admin-empty">{t("Ładowanie...")}</div>;

  return (
    <>
      <div className="admin-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "users"}
          onClick={() => switchTab("users")}
        >
          {t("Użytkownicy")}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "roles"}
          onClick={() => switchTab("roles")}
        >
          {t("Role i Uprawnienia")}
        </button>
      </div>

      {activeTab === "users" ? (
        <>
          <div className="admin-page__header">
            <div>
              <h2>{t("Zarządzanie Użytkownikami")}</h2>
            </div>
            <div className="admin-page__actions">
              <button
                className={showAddForm ? "admin-btn admin-btn--ghost" : "admin-btn"}
                onClick={() => setShowAddForm(!showAddForm)}
              >
                {showAddForm ? t("Anuluj") : <><UserPlus size={16} /> {t("Dodaj użytkownika")}</>}
              </button>
            </div>
          </div>

          {showAddForm && (
            <form className="admin-card user-add" onSubmit={handleAddUser}>
              <div className="admin-form-row">
                <div className="admin-field">
                  <label htmlFor="new-username">{t("Login")}</label>
                  <input
                    id="new-username"
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="new-password">{t("Hasło")}</label>
                  <input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    placeholder={t("at least 8 characters")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </div>
                <div className="admin-field">
                  <label htmlFor="new-role">{t("Rola")}</label>
                  <select id="new-role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                    {roles.map((r) => (
                      <option key={r.id} value={r.role_name}>{r.role_name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="user-add__actions">
                <button type="submit" className="admin-btn">{t("Zapisz")}</button>
              </div>
            </form>
          )}

          {error && <div className="admin-alert admin-alert--error">{error}</div>}
          {resetMessage && <div className="admin-alert admin-alert--success">{resetMessage}</div>}

          <div className="admin-card">
            <table className="admin-table user-table">
              <thead>
                <tr>
                  <th>{t("Użytkownik")}</th>
                  <th>{t("Rola")}</th>
                  <th>{t("Data utworzenia")}</th>
                  <th className="user-table__actions">{t("Akcje")}</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <React.Fragment key={user.id}>
                    <tr>
                      <td>
                        <strong>{user.username}</strong>
                        {user.username === currentUsername && (
                          <span className="admin-chip user-chip">{t("your account")}</span>
                        )}
                      </td>
                      <td><span className="admin-code">{user.role}</span></td>
                      <td>{new Date(user.created_at).toLocaleString()}</td>
                      <td className="user-table__actions">
                        <button
                          className="admin-btn admin-btn--ghost"
                          onClick={() => {
                            setResetUserId(resetUserId === user.id ? null : user.id);
                            setResetPassword("");
                            setResetMessage(null);
                            setError(null);
                          }}
                        >
                          <KeyRound size={15} /> {t("Reset password")}
                        </button>
                        <button
                          className="admin-btn admin-btn--danger"
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          disabled={user.username === "admin" || user.username === currentUsername}
                          title={user.username === currentUsername ? t("You cannot delete your own account.") : undefined}
                        >
                          <Trash2 size={15} /> {t("Usuń")}
                        </button>
                      </td>
                    </tr>
                    {resetUserId === user.id && (
                      <tr className="user-reset-row">
                        <td colSpan={4}>
                          <form className="user-reset" onSubmit={(e) => handleResetPassword(e, user)}>
                            <div className="admin-field user-reset__field">
                              <label htmlFor={`reset-password-${user.id}`}>
                                {t("New password for")} <strong>{user.username}</strong>
                              </label>
                              <input
                                id={`reset-password-${user.id}`}
                                type="password"
                                autoComplete="new-password"
                                placeholder={t("at least 8 characters")}
                                value={resetPassword}
                                onChange={(e) => setResetPassword(e.target.value)}
                                autoFocus
                              />
                            </div>
                            <button type="submit" className="admin-btn">{t("Zapisz")}</button>
                            <button
                              type="button"
                              className="admin-btn admin-btn--ghost"
                              onClick={() => {
                                setResetUserId(null);
                                setError(null);
                              }}
                            >
                              {t("Anuluj")}
                            </button>
                          </form>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <div className="admin-page__header">
            <div>
              <h2>{t("Role i Uprawnienia")}</h2>
              <p>{t("The admin role always has all permissions.")}</p>
            </div>
          </div>

          {error && <div className="admin-alert admin-alert--error">{error}</div>}

          {/* Macierz rola x uprawnienie - w kolumnach role, w wierszach uprawnienia */}
          <div className="admin-card">
            <div className="role-matrix-wrap">
              <table className="admin-table role-matrix">
                <thead>
                  <tr>
                    <th>{t("Uprawnienia")}</th>
                    {roles.map((role) => (
                      <th key={role.id} className="role-matrix__role">
                        {role.role_name}
                        {role.role_name === "admin" && <span className="role-matrix__hint">{t("locked")}</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {allPossiblePermissions.map((perm) => (
                    <tr key={perm}>
                      <td className="role-matrix__perm">{t(perm)}</td>
                      {roles.map((role) => {
                        const checked = role.permissions.includes(perm);
                        const locked = role.role_name === "admin";
                        return (
                          <td key={role.id} className="role-matrix__cell">
                            <label
                              className={`role-check${checked ? " role-check--on" : ""}${locked ? " role-check--locked" : ""}`}
                              title={`${role.role_name} · ${t(perm)}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleTogglePermission(role.id, role.permissions, perm)}
                                disabled={locked}
                              />
                              <span className="role-check__box" aria-hidden="true">
                                {checked && <Check size={14} strokeWidth={3} />}
                              </span>
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default UserManagement;
